import bs58check from 'bs58check';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha512 } from '@noble/hashes/sha512.js';
import * as bitcoin from 'bitcoinjs-lib';

// Decodes a base58check-encoded extended pubkey (as returned by Ledger's
// getWalletXpub) into its raw components. The "parent fingerprint" bytes
// identify the immediate parent node (m/84'/0' for our account paths), which
// is invariant across account groups on the same device/seed but differs
// across different physical Ledgers — used to key the local address cache
// per-device so accounts from different Ledgers never collide.
export function parseXpub(xpubBase58) {
  // bs58check.decode() returns a plain Uint8Array in the browser (not a
  // Buffer), and Uint8Array.prototype.toString('hex') silently ignores the
  // encoding argument — wrap in Buffer.from() to get real hex out of it.
  const buf = Buffer.from(bs58check.decode(xpubBase58));
  return {
    parentFingerprintHex: buf.subarray(5, 9).toString('hex'),
    chainCodeHex: buf.subarray(13, 45).toString('hex'),
    pubkeyHex: buf.subarray(45, 78).toString('hex'),
  };
}

// Guards against stale/corrupt cache entries (e.g. written before a hex-encoding
// bugfix) reaching the crypto layer, where a malformed value throws an opaque error.
export function isValidHex(str) {
  return typeof str === 'string' && str.length > 0 && str.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(str);
}

function ser32(i) {
  const b = Buffer.alloc(4);
  b.writeUInt32BE(i, 0);
  return b;
}

// BIP32 public-key-only child derivation (CKDpub) — derives child addresses
// from an account-level pubkey/chaincode without needing the hardware wallet
// for every address, since the wallet only ever needs to sign at the leaf path.
function ckdPub(pubkeyHex, chainCodeHex, index) {
  if (index >= 0x80000000) throw new Error('Cannot derive a hardened child from a public key');
  const chainCode = Buffer.from(chainCodeHex, 'hex');
  const point = secp256k1.ProjectivePoint.fromHex(pubkeyHex);
  const compressed = Buffer.from(point.toRawBytes(true));
  const data = Buffer.concat([compressed, ser32(index)]);
  const I = hmac(sha512, chainCode, data);
  const IL = I.subarray(0, 32);
  const IR = I.subarray(32, 64);
  const ilPoint = secp256k1.ProjectivePoint.BASE.multiply(BigInt('0x' + Buffer.from(IL).toString('hex')));
  const childPoint = ilPoint.add(point);
  return {
    pubkeyHex: Buffer.from(childPoint.toRawBytes(true)).toString('hex'),
    chainCodeHex: Buffer.from(IR).toString('hex'),
  };
}

// Derives the bech32 (P2WPKH) address at .../<change>/<addressIndex> from an
// account node's {publicKey, chainCode} (as returned by Ledger's
// getWalletPublicKey at the account-level path, e.g. 84'/0'/N').
export function deriveAddress(accountPubkeyHex, accountChainCodeHex, addressIndex, change = 0) {
  const changeKey = ckdPub(accountPubkeyHex, accountChainCodeHex, change);
  const addrKey = ckdPub(changeKey.pubkeyHex, changeKey.chainCodeHex, addressIndex);
  const { address } = bitcoin.payments.p2wpkh({ pubkey: Buffer.from(addrKey.pubkeyHex, 'hex') });
  return address;
}
